// 建設モードの UI（画面下のデコパレット + 庭属性表示）。
//
// DOM を直接組み立てる軽量実装（React なし方針）。状態は持たず、main から
// 「選択中デコ」「チップ残高」「属性合計」を受けて描画を更新するだけ。
// 選択変更は onSelect コールバックで通知する。
//
// タッチを一級市民として扱う: ボタンは min 44px、横スクロールはネイティブ。

import { DECO_CATALOG } from '../sim/decos';
import { ATTRIBUTE_KEYS, ATTRIBUTE_LABELS, type AttributeKey } from '../sim/decos';
import { decoThumbnail } from '../render/thumbnail';

export interface BuildPanel {
  /** 現在選択中のデコ ID（未選択は null）。 */
  readonly selectedId: string | null;
  /** チップ残高に応じて各ボタンの購入可否表示を更新する。 */
  updateAffordability(chips: number): void;
  /** 庭属性表示を更新する。 */
  updateAttributes(total: Record<AttributeKey, number>): void;
  /** 選択を外す（撤去後など）。 */
  clearSelection(): void;
}

export interface BuildPanelOptions {
  paletteEl: HTMLElement;
  /**
   * 庭属性のテキスト表示先（任意）。v0.6.0 で生数値 HUD（#attrs）を廃止し、属性は
   * ステータス画面に集約したので通常は渡さない。互換のため updateAttributes は残す。
   */
  attrsEl?: HTMLElement | null;
  /** デコ選択が変わったとき（null = 解除）。 */
  onSelect: (decoId: string | null) => void;
}

export function createBuildPanel(opts: BuildPanelOptions): BuildPanel {
  const { paletteEl, attrsEl, onSelect } = opts;
  paletteEl.innerHTML = '';

  let selectedId: string | null = null;
  const buttons = new Map<string, HTMLButtonElement>();
  const costById = new Map<string, number>();

  function select(id: string | null): void {
    selectedId = id;
    for (const [bid, btn] of buttons) {
      btn.classList.toggle('selected', bid === id);
    }
    onSelect(id);
  }

  for (const deco of DECO_CATALOG) {
    if (deco.hidden) continue; // 入口ゲート等の特殊デコはパレットに出さない（v0.7.0）
    costById.set(deco.id, deco.cost);
    const btn = document.createElement('button');
    btn.className = 'deco-btn';
    btn.type = 'button';
    btn.dataset.deco = deco.id;
    btn.setAttribute('aria-label', `${deco.name}（${deco.cost}チップ）`);

    // アイコンはデコの実メッシュをオフスクリーンレンダした透過サムネ（絵文字をやめる）。
    const ic = document.createElement('img');
    ic.className = 'ic';
    ic.alt = deco.name;
    ic.draggable = false;
    ic.src = decoThumbnail(deco.id);
    const nm = document.createElement('span');
    nm.className = 'nm';
    nm.textContent = deco.name;
    const ct = document.createElement('span');
    ct.className = 'ct';
    ct.textContent = `💰${deco.cost}`;
    btn.append(ic, nm, ct);

    // クリック/タップ統一: トグル選択（同じデコ再タップで解除）
    btn.addEventListener('click', () => {
      select(selectedId === deco.id ? null : deco.id);
    });

    buttons.set(deco.id, btn);
    paletteEl.appendChild(btn);
  }

  function updateAffordability(chips: number): void {
    for (const [id, btn] of buttons) {
      const cost = costById.get(id) ?? 0;
      btn.classList.toggle('unaffordable', chips < cost);
    }
  }

  function updateAttributes(total: Record<AttributeKey, number>): void {
    if (!attrsEl) return; // v0.6.0: 属性はステータス画面へ集約（HUD には出さない）
    const parts: string[] = [];
    for (const key of ATTRIBUTE_KEYS) {
      const v = total[key];
      if (v > 0) parts.push(`${ATTRIBUTE_LABELS[key]} ${v}`);
    }
    attrsEl.textContent = parts.length ? parts.join('　') : '庭はまだ静か';
  }

  function clearSelection(): void {
    select(null);
  }

  return {
    get selectedId() {
      return selectedId;
    },
    updateAffordability,
    updateAttributes,
    clearSelection,
  };
}
